import { get, has, merge } from 'lodash';
import {
  CHART_COLORS,
  VEGA_SPEC_AXIS_SETTINGS,
  VEGA_SPEC_LEGEND_SETTINGS,
} from 'constants/chart-spec';
import { CHART_CATEGORIES } from 'constants/chart-categories';
import { INFERRED_TYPE_ENUM } from 'constants/chart-channels';
import { generateVegaTooltip } from 'utils/chart/tooltips';
import {
  generateVegaSortParams,
  generateVegaTypeCoercionTransforms,
} from 'utils/chart/transforms';
import { generateVegaLabelTextExpression } from 'utils/chart/vega-expressions';
import { getScaleMinMaxProps } from 'utils/chart/scale-min-max';

export default {
  name: 'Scatter',
  displayName: 'Scatter',
  order: 1,
  flavour: 'continuous',
  iconValue: 'scatter',
  category: CHART_CATEGORIES.GRID,
  channels: [
    {
      name: 'x',
      displayName: 'X Axis',
      required: true,
      channelType: 'value',
      intent: 'x-value',
    },
    {
      name: 'y',
      displayName: 'Y Axis',
      required: true,
      channelType: 'value',
      intent: 'y-value',
    },
    {
      name: 'size',
      displayName: 'Size',
      required: false,
      channelType: 'aggregation',
    },
    {
      name: 'color',
      displayName: 'Color',
      required: false,
      channelType: 'category',
      intent: 'series',
    },
    {
      name: 'shape',
      displayName: 'Shape',
      required: false,
      channelType: 'category',
    },
  ],
  specFn: (encodings, customisations) => {
    const COLORS =
      get(customisations.options, 'colorDiscrete.value') || CHART_COLORS;
    const sortParams = generateVegaSortParams(encodings);

    const isLogScaleEnabled = c =>
      get(customisations.axes, `${c}.logScale.enabled`, false);
    const isDate = c => {
      const { inferredType, transformedType } = get(encodings, c, {});
      return (transformedType || inferredType) === INFERRED_TYPE_ENUM.DATE;
    };
    const scaleType = c => {
      if (isDate(c)) {
        return 'utc';
      }
      return isLogScaleEnabled(c) ? 'log' : 'linear';
    };
    // do not pass logScale because we always want Vega to
    // decide the min value even if log scale has been enabled
    const xAxisProps = getScaleMinMaxProps(customisations, 'x');
    const yAxisProps = getScaleMinMaxProps(customisations, 'y');

    const markUpdate = {
      fill: { value: 'transparent' },
      x: { scale: 'x', field: 'x' },
      y: { scale: 'y', field: 'y' },
      stroke: { value: COLORS[0] },
      tooltip: { signal: generateVegaTooltip(encodings, customisations) },
    };
    const scales = [
      {
        name: 'x',
        type: scaleType('x'),
        domain: { data: 'transformedData', field: 'x' },
        range: [0, { signal: 'width' }],
        zero: false,
        ...xAxisProps,
      },
      {
        name: 'y',
        type: scaleType('y'),
        domain: { data: 'transformedData', field: 'y' },
        range: [{ signal: 'height' }, 0],
        zero: false,
        ...yAxisProps,
      },
    ];
    if (encodings.size) {
      markUpdate.size = { scale: 'size', field: 'size' };
      scales.push({
        name: 'size',
        type: 'linear',
        domain: { data: 'transformedData', field: 'size' },
        range: [0, 361],
        nice: false,
        zero: true,
      });
    }
    if (encodings.color) {
      markUpdate.stroke = { scale: 'color', field: 'color' };
      scales.push({
        name: 'color',
        type: 'ordinal',
        domain: {
          data: 'transformedData',
          field: 'color',
          sort: sortParams.color,
        },
        range: COLORS,
      });
    }
    if (encodings.shape) {
      markUpdate.shape = { scale: 'shape', field: 'shape' };
      scales.push({
        name: 'shape',
        type: 'ordinal',
        domain: {
          data: 'transformedData',
          field: 'shape',
          sort: sortParams.shape,
        },
        range: 'symbol',
      });
    }
    return {
      autosize: {
        type: 'fit',
        contains: 'padding',
      },
      style: 'cell',
      data: [
        {
          name: 'transformedData',
          source: 'values',
          transform: [
            ...generateVegaTypeCoercionTransforms('x', encodings),
            ...generateVegaTypeCoercionTransforms('y', encodings),
            ...(has(encodings, 'size')
              ? generateVegaTypeCoercionTransforms('size', encodings)
              : []),
          ],
        },
      ],
      marks: [
        {
          name: 'marks',
          type: 'symbol',
          style: ['point'],
          from: { data: 'transformedData' },
          encode: {
            update: markUpdate,
          },
        },
      ],
      scales: scales,
      axes: [
        merge({}, VEGA_SPEC_AXIS_SETTINGS, {
          scale: 'x',
          orient: 'bottom',
          grid: true,
          title: get(encodings, 'x.axis.title'),
          tickCount: { signal: 'ceil(width/60)' },
          encode: {
            labels: {
              update: {
                text: {
                  signal: generateVegaLabelTextExpression(
                    encodings.x,
                    customisations.channels.x,
                    { withDecimals: false, withSpacePadding: true }
                  ),
                },
              },
            },
          },
        }),
        merge({}, VEGA_SPEC_AXIS_SETTINGS, {
          scale: 'y',
          orient: 'left',
          grid: true,
          title: get(encodings, 'y.axis.title'),
          tickCount: { signal: 'ceil(height/60)' },
          encode: {
            labels: {
              update: {
                text: {
                  signal: generateVegaLabelTextExpression(
                    encodings.y,
                    customisations.channels.y,
                    { withDecimals: false }
                  ),
                },
              },
            },
          },
        }),
      ],
      legends: [
        ...(!has(encodings, 'color.legend.title')
          ? []
          : [
              merge({}, VEGA_SPEC_LEGEND_SETTINGS, {
                title: get(encodings, 'color.legend.title'),
                fill: 'color',
                encode: {
                  labels: {
                    update: {
                      text: {
                        signal: generateVegaLabelTextExpression(
                          encodings.color,
                          customisations.channels.color
                        ),
                      },
                    },
                  },
                },
              }),
            ]),
        ...(!has(encodings, 'size.legend.title')
          ? []
          : [
              {
                title: get(encodings, 'size.legend.title'),
                size: 'size',
                encode: {
                  labels: {
                    update: {
                      text: {
                        signal: generateVegaLabelTextExpression(
                          encodings.size,
                          customisations.channels.size,
                          { withDecimals: false }
                        ),
                      },
                    },
                  },
                  symbols: {
                    update: {
                      strokeWidth: { value: 3 },
                      fill: { value: 'transparent' },
                    },
                  },
                },
              },
            ]),
        ...(!has(encodings, 'shape.legend.title')
          ? []
          : [
              {
                title: get(encodings, 'shape.legend.title'),
                shape: 'shape',
                encode: {
                  symbols: {
                    update: {
                      strokeWidth: { value: 3 },
                      fill: { value: 'transparent' },
                    },
                  },
                  labels: {
                    update: {
                      text: {
                        signal: generateVegaLabelTextExpression(
                          encodings.shape,
                          customisations.channels.shape
                        ),
                      },
                    },
                  },
                },
              },
            ]),
      ],
    };
  },
};
